import React, { useState } from "react";

// For route param reading
import { useLocation } from "react-router-dom";

const PRIMARY_COLOR = "#ebcab3"; // background
const ACCENT_COLOR = "#3d0707"; // brand accent

// Password requirement rules
const criteria = [
    { regex: /.{8,}/, message: 'At least 8 characters' },
    { regex: /[A-Z]/, message: 'An uppercase letter' },
    { regex: /[a-z]/, message: 'A lowercase letter' },
    { regex: /[0-9]/, message: 'A number' },
    { regex: /[^A-Za-z0-9]/, message: 'A special character' },
];

function getPasswordStrength(password) {
    if (!password) return { label: "Enter a password", color: "#acadaf", score: 0, criteriaMet: [] };
    let met = criteria.filter(c => c.regex.test(password));
    let score = met.length;
    let color, label;
    if (score <= 2) {
        label = "Weak";
        color = "#dc2626";
    }
    else if (score === 3) {
        label = "Moderate";
        color = "#eab308";
    } else {
        label = "Strong";
        color = "#16a34a";
    }
    return { label, color, score, criteriaMet: met.map(c => c.message) };
}

function useLsbTokenFromUrl() {
    // Use /reset-password?lsb_token=abcd1234 style
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    return params.get("lsb_token") || "";
}

const ResetPassword = () => {
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const passwordStrength = getPasswordStrength(newPassword);
    const metCriteriaSet = new Set(passwordStrength.criteriaMet);
    const lsbToken = useLsbTokenFromUrl();

    // Submit to real endpoint
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!newPassword || !confirmPassword) {
            setError("Both fields are required");
            return;
        }
        // Validate password by all criteria
        for (let c of criteria) {
            if (!c.regex.test(newPassword)) {
                setError(`Password must include: ${c.message}`);
                return;
            }
        }
        if (newPassword !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }
        if (!lsbToken) {
            setError("Invalid or missing password reset token in the link.");
            return;
        }

        setIsLoading(true);

        // POST to API with lsb_token
        try {
            const res = await fetch('https://portal.lsb.go.ke/api/change_password/', {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    new_password: newPassword,
                    lsb_token: lsbToken
                }),
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.detail || data.error || "Failed to reset password.");
                setIsLoading(false);
                return;
            }
            setSuccess("Password reset successfully.");
            setNewPassword("");
            setConfirmPassword("");
        } catch (err) {
            setError("Failed to reset password. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex justify-center items-center bg-[#faf6f3] px-4">
            <form
                onSubmit={handleSubmit}
                className="max-w-md w-full bg-white shadow-md rounded-xl px-8 py-10"
                style={{ border: `2.5px solid ${ACCENT_COLOR}` }}
            >
                <h2
                    className="text-2xl font-bold mb-8 text-center"
                    style={{ color: ACCENT_COLOR, letterSpacing: ".04em" }}
                >
                    Reset Password
                </h2>
                {error && (
                    <div className="mb-4 text-center text-red-600 text-sm font-medium">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="mb-4 text-center text-green-700 text-sm font-medium">
                        {success}
                    </div>
                )}
                {/* New Password */}
                <div className="mb-6">
                    <label
                        htmlFor="newPassword"
                        className="block text-sm font-semibold mb-1"
                        style={{ color: ACCENT_COLOR }}
                    >
                        New Password
                    </label>
                    <div className="relative">
                        <input
                            type={showPassword ? "text" : "password"}
                            id="newPassword"
                            name="newPassword"
                            className="w-full rounded-lg border shadow-sm py-3 pl-4 pr-11 text-sm focus:outline-none"
                            style={{
                                border: `1.5px solid ${PRIMARY_COLOR}`,
                                background: "#fff8f3",
                                color: ACCENT_COLOR,
                                letterSpacing: ".06em",
                            }}
                            placeholder="Enter new password"
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                        />
                        <button
                            type="button"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#b27849] text-lg focus:outline-none"
                            tabIndex={-1}
                            style={{ background: "transparent" }}
                        >
                            {showPassword ? (
                                <i className="fa-regular fa-eye" />
                            ) : (
                                <i className="fa-regular fa-eye-slash" />
                            )}
                        </button>
                    </div>
                    {/* Password strength meter */}
                    <div className="mt-2">
                        <div
                            className="w-full h-2 rounded transition-all"
                            style={{
                                background:
                                    passwordStrength.score === 0
                                        ? "#f3f4f6"
                                        : passwordStrength.color + "33",
                            }}
                        >
                            <div
                                style={{
                                    height: "100%",
                                    width:
                                        passwordStrength.score === 0
                                            ? "0%"
                                            : `${passwordStrength.score * 20}%`,
                                    background: passwordStrength.color,
                                    borderRadius: 4,
                                    transition: "width 300ms",
                                }}
                            />
                        </div>
                        <div
                            className="mt-1 text-md font-medium"
                            style={{
                                color: passwordStrength.color
                            }}
                        >
                            {passwordStrength.label}
                        </div>
                        {/* Criteria checklist */}
                        <ul className="mt-1 text-xs">
                            {criteria.map((c) => (
                                <li
                                    key={c.message}
                                    style={{
                                        color: metCriteriaSet.has(c.message) ? "#16a34a" : "#b27849",
                                        fontWeight: metCriteriaSet.has(c.message) ? 600 : 400,
                                        letterSpacing: ".015em"
                                    }}
                                    className="flex items-center gap-1"
                                >
                                    {metCriteriaSet.has(c.message) ? (
                                        <span style={{ fontWeight: "bold" }}>✓</span>
                                    ) : (
                                        <span style={{ opacity: 0.5 }}>○</span>
                                    )}{" "}
                                    {c.message}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                {/* Confirm Password */}
                <div className="mb-8">
                    <label
                        htmlFor="confirmPassword"
                        className="block text-sm font-semibold mb-1"
                        style={{ color: ACCENT_COLOR }}
                    >
                        Confirm New Password
                    </label>
                    <div className="relative">
                        <input
                            type={showConfirm ? "text" : "password"}
                            id="confirmPassword"
                            name="confirmPassword"
                            className="w-full rounded-lg border shadow-sm py-3 pl-4 pr-11 text-sm focus:outline-none"
                            style={{
                                border: `1.5px solid ${PRIMARY_COLOR}`,
                                background: "#fff8f3",
                                color: ACCENT_COLOR,
                                letterSpacing: ".06em",
                            }}
                            placeholder="Confirm new password"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                        />
                        <button
                            type="button"
                            aria-label={showConfirm ? "Hide password" : "Show password"}
                            onClick={() => setShowConfirm((v) => !v)}
                            className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#b27849] text-lg focus:outline-none"
                            tabIndex={-1}
                            style={{ background: "transparent" }}
                        >
                            {showConfirm ? (
                                <i className="fa-regular fa-eye" />
                            ) : (
                                <i className="fa-regular fa-eye-slash" />
                            )}
                        </button>
                    </div>
                    {/* Show confirmation match feedback */}
                    {confirmPassword && (
                        <div
                            className="mt-1 text-xs font-medium"
                            style={{
                                color:
                                    newPassword && confirmPassword
                                        ? newPassword === confirmPassword
                                            ? "#16a34a"
                                            : "#dc2626"
                                        : "#acadaf"
                            }}
                        >
                            {newPassword && confirmPassword
                                ? newPassword === confirmPassword
                                    ? "Passwords match"
                                    : "Passwords do not match"
                                : ""}
                        </div>
                    )}
                </div>
                {/* Submit */}
                <button
                    type="submit"
                    className="w-full rounded font-bold py-3 px-7 uppercase tracking-wide text-lg transition"
                    style={{
                        background: ACCENT_COLOR,
                        color: PRIMARY_COLOR,
                        boxShadow: "0 3px 18px #3d07071c",
                        letterSpacing: ".08em",
                        border: `2.5px solid ${PRIMARY_COLOR}`,
                    }}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <span
                            className="inline-block align-middle"
                            style={{
                                width: 24,
                                height: 24,
                                border: `3px solid #b27849`,
                                borderTop: `3px solid ${ACCENT_COLOR}`,
                                borderRadius: "50%",
                                animation: "spin 1s linear infinite",
                            }}
                        />
                    ) : (
                        "Reset Password"
                    )}
                </button>
                <style>{`
          @media (max-width: 600px) {
            .max-w-md { max-width: 97vw; }
            .px-8 { padding-left: 1.25rem; padding-right: 1.25rem;}
          }
          @keyframes spin {
            from { transform: rotate(0deg);}
            to { transform: rotate(360deg);}
          }
        `}</style>
            </form>
        </div>
    );
};

export default ResetPassword;